import { useMemo, useState } from "react";
import { ArrowUpDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmt } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

const NUMERIC = ["rp", "pct", "num", "min", "score"];

function useSorted(rows, sort) {
  return useMemo(() => {
    if (!sort.key) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = a[sort.key], y = b[sort.key];
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === "number" ? x - y : String(x).localeCompare(String(y))) * dir;
    });
  }, [rows, sort]);
}

export const DataTable = ({ table, title = "Tabel Rinci", onRowClick, rowHint }) => {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const size = table.page_size || 15;
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return table.rows;
    return table.rows.filter((r) => table.columns.some((c) => c.fmt === "text" && String(r[c.key] ?? "").toLowerCase().includes(s)));
  }, [q, table]);
  const rows = useSorted(filtered, sort);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const cur = Math.min(page, pages - 1);
  const slice = rows.slice(cur * size, cur * size + size);
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div data-testid="data-table" className="rounded-xl border bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-heading text-base sm:text-lg font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{rows.length} baris{rowHint ? ` · ${rowHint}` : ""}</p>
        </div>
        <div className="relative sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="table-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Cari…" className="pl-8" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {table.columns.map((c) => (
                <th key={c.key} className={`whitespace-nowrap px-3 py-2.5 font-medium ${NUMERIC.includes(c.fmt) ? "text-right" : "text-left"}`}>
                  <button data-testid={`table-sort-${c.key}`} onClick={() => toggle(c.key)} className="inline-flex items-center gap-1 hover:text-foreground">
                    {c.label}<ArrowUpDown className="h-3 w-3 opacity-50" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 && (
              <tr><td colSpan={table.columns.length} className="px-3 py-10 text-center text-muted-foreground">Tidak ada data untuk filter ini.</td></tr>
            )}
            {slice.map((r, i) => (
              <tr key={i} data-testid={`table-row-${cur * size + i}`} onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={`border-t transition-colors hover:bg-muted/50 ${onRowClick ? "cursor-pointer" : ""} ${r.lonjakan ? "bg-rose-50 dark:bg-rose-950/30" : ""}`}>
                {table.columns.map((c) => (
                  <td key={c.key} className={`whitespace-nowrap px-3 py-2.5 ${NUMERIC.includes(c.fmt) ? "font-num text-right" : ""} ${c.key === table.columns[0].key ? "font-medium" : ""}`}>
                    {c.fmt === "status" ? <StatusBadge status={r[c.key]} />
                      : c.key === "lonjakan" && r[c.key] ? <span className="font-semibold text-rose-600">{r[c.key]}</span> : fmt(r[c.key], c.fmt)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t px-4 py-3 text-xs text-muted-foreground">
        <span>Halaman {cur + 1} dari {pages}</span>
        <div className="flex gap-2">
          <Button data-testid="table-prev-page" size="sm" variant="outline" disabled={cur === 0} onClick={() => setPage(cur - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button data-testid="table-next-page" size="sm" variant="outline" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
};
